/**
 * @CODE:ACC-003-HOOKS-001
 * useShareDialog Hook
 *
 * Custom hook for sharing accommodation details
 * Features:
 * - Uses native Web Share API (navigator.share)
 * - Fallback to clipboard copy
 * - Loading and error state management
 * - Constructs shareable URL and text
 */

import { useState } from 'react';

interface UseShareDialogOptions {
  accommodationId: number;
  accommodationName: string;
}

interface UseShareDialogReturn {
  isLoading: boolean;
  error: Error | null;
  share: () => Promise<void>;
}

/**
 * Hook to manage accommodation sharing
 * @param options - Options with accommodation ID and name
 * @returns Object with share function and state
 */
export function useShareDialog({
  accommodationId,
  accommodationName,
}: UseShareDialogOptions): UseShareDialogReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const share = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const shareUrl = `${window.location.origin}/stay/${accommodationId}`;
      const shareTitle = `${accommodationName} - Wholsee Trek Adventures`;
      const shareText = `Check out this accommodation: ${accommodationName}`;

      // Try native Share API first
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } else if (navigator.clipboard) {
        // Fallback to clipboard
        await navigator.clipboard.writeText(`${shareTitle}\n${shareUrl}`);
      } else {
        throw new Error('Share API not available');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to share accommodation');
      // Don't set error if user cancelled share dialog (AbortError)
      if (error.name !== 'AbortError') {
        setError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    share,
  };
}
